import { VersionRange } from '../../models/core/version-range.js';
import { DataModelBuilder, SerializationContext, SerializedData } from '../../models/dto/dto.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { Component } from '../core/component.js';

export enum EventDateFormat {
  DateTime = 'DateTime',
  DateOnly = 'DateOnly',
  Soon = 'Soon',
}

export class DateFormatComponent extends Component {
  static readonly TYPE_ID: string = '045594ad70bb4745961ef20ea6784dfd';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
    public readonly format: EventDateFormat,
  ) {
    super();
  }

  get $removable(): boolean {
    return true;
  }

  get $typeId(): string {
    return DateFormatComponent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(DateFormatComponent.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): DateFormatComponent {
    return new DateFormatComponent(
        toEnumFromString(MapUtils.getString(map, 'format'), EventDateFormat),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'format': fromEnumAsString(this.format),
    };
  }

  clone(): this {
    return new DateFormatComponent(
        this.format,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.format === this.format;
  }
}

export class DateFormatComponentBuilder extends DataModelBuilder<DateFormatComponent> {
  build(context: SerializationContext, map: SerializedData): DateFormatComponent {
    return DateFormatComponent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    this.migrateEnum(map, 'format', EventDateFormat, EventDateFormat.DateTime);

    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(DateFormatComponent.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(DateFormatComponent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    /**
     * Version 2:
     *  - parameter `showTime` type {@link boolean} was replaced with parameter `format` of type {@link EventDateFormat}
     *  - default value of `format` is [EventDateFormat.DateTime]
     */
    if(version < 2) {
      map['format'] ??= MapUtils.getBoolean(map, 'showTime') ?
        fromEnumAsString(EventDateFormat.DateTime) :
        fromEnumAsString(EventDateFormat.DateOnly);

      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }

  migrateFromCurrentSchemaVersion(
      context: SerializationContext,
      model: DateFormatComponent,
      map: SerializedData,
  ): SerializedData {
    let version = model.$schemaVersion.to ?? DateFormatComponent.SCHEMA_VERSION;

    /**
     * Version 1:
     *  - parameter `format` of type {@link EventDateFormat} was replaced with parameter `showTime` type {@link boolean}
     *  - default value of `showTime` is [true]
     */
    if(version >= 2) {
      version = 1;

      map['showTime'] = model.format !== EventDateFormat.DateOnly;
      map['$schema_version'] = VersionRange.encodedBetween(1, 2);
    }

    return map;
  }
}
